/**
 * Liquidity-locker registry.
 *
 * One entry per (chain × DEX) deployment of IdraLiquidityLocker. The /liquid page
 * reads locks directly from each locker's on-chain view functions, so adding a new
 * chain or DEX later is just another entry here (plus its VITE_ address env var).
 */

export interface LockerConfig {
  chainId: number;
  chainLabel: string;
  dexLabel: string;
  lockerAddress: string;
  rpcUrl: string;
  explorerUrl: string;
  /** Deep link to the DEX's add-liquidity page for the IDRA/USDC pair. */
  addLiquidityUrl: (idra: string, usdc: string) => string;
  /** Deep link to the DEX's remove-liquidity page (burn LP → underlying). */
  removeLiquidityUrl: (idra: string, usdc: string) => string;
  /** Deep link to the DEX's swap page (buy IDRA with USDC). */
  swapUrl: (idra: string, usdc: string) => string;
}

const ZERO = '0x0000000000000000000000000000000000000000';
const isAddress = (v?: string): v is string =>
  !!v && /^0x[a-fA-F0-9]{40}$/.test(v) && v.toLowerCase() !== ZERO;

const MAINNET_LOCKER = import.meta.env.VITE_IDRA_LIQUIDITY_LOCKER_MAINNET as string | undefined;
const MAINNET_RPC =
  (import.meta.env.VITE_POLYGON_RPC_URL_MAINNET as string | undefined) || 'https://polygon-rpc.com';

export const LOCKERS: LockerConfig[] = [
  ...(isAddress(MAINNET_LOCKER)
    ? [
        {
          chainId: 137,
          chainLabel: 'Polygon',
          dexLabel: 'QuickSwap V2',
          lockerAddress: MAINNET_LOCKER,
          rpcUrl: MAINNET_RPC,
          explorerUrl: 'https://polygonscan.com',
          // QuickSwap moved its app to dapp.quickswap.exchange (the old
          // quickswap.exchange/#/... routes now just hit a marketing page).
          // Add liquidity opens the Pool wizard; remove/manage a V2 LP goes through
          // Your Positions → filter v2 → Import → Remove. Swap preselects the pair.
          addLiquidityUrl: () => `https://dapp.quickswap.exchange/pool?chainId=137`,
          removeLiquidityUrl: () =>
            `https://dapp.quickswap.exchange/pool/positions?chainId=137`,
          swapUrl: (idra: string, usdc: string) =>
            `https://dapp.quickswap.exchange/swap?type=best&from=${usdc}&to=${idra}&chainId=137`,
        },
      ]
    : []),
];
